import SwiperGalleryModal from 'components/Modal/gallery/SwiperGalleryModal'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import LoginRequiredModal from 'components/Modal/LoginRequiredModal'
import SaveProfileModal from 'components/Modal/SaveProfileModal'
import { useContext, useEffect, useRef, useState } from 'react'
import SetPublishModal from 'components/Modal/SetPublishModal'
import AddPhotosModal from 'components/Modal/AddPhotosModal'
import AddPostModal from 'components/Modal/AddPostModal'
import CandlesModal from 'components/Modal/CandlesModal'
import ShareButton from 'components/Button/ShareButton'
import AddGpsModal from 'components/Modal/AddGpsModal'
import { useLanguage } from 'contexts/LanguageContext'
import FromQrModal from 'components/Modal/FromQrModal'
import { AlertContext } from 'contexts/AlertContext'
import useClickOutside from 'hooks/useClickOutside'
import useTranslation from 'hooks/useTranslation'
import { useAuth } from 'contexts/AuthContext'
import Skeleton from 'components/Skeleton'
import QrScanner from 'pages/QrScanner'
import { openMap } from 'utils/openMap'
import Button from 'components/Button'
import TopBar from 'components/Topbar'
import useStore from 'hooks/useStore'
import { GET, POST } from 'api/request'
import * as urls from 'api/urls'
import Tabs from './Tabs'
import './index.scss'

interface DateParts {
  day: string
  monthName: string
  year: string
}

const Profile: React.FC = () => {
  const { t } = useTranslation('profile')
  const [language] = useLanguage()

  const [isScanner, showScanner] = useState(window.location.search.includes('scanner'))
  const [isCandleButtonVisible, setCandleButtonVisible] = useState<boolean>(false)
  const [isDropdownVisible, setDropdownVisible] = useState<boolean>(false)
  const [isShareVisible, setShareVisible] = useState<boolean>(false)
  const [data, setData] = useState<board.BoardResponse | null>()
  const [initialSlide, setInitialSlide] = useState<number>(0)
  const [isLoading, setLoading] = useState<boolean>(true)
  const [activeTab, setActiveTab] = useState('biography')
  const [saveType, setSaveType] = useState('save')
  const [timeLeft, setTimeLeft] = useState('')
  const [isModal, setModal] = useState('')
  

  const [post, setPost] = useState<book.BookEntry[]>([])
  const [postPage, setPostPage] = useState(1)
  const [postHasMore, setPostHasMore] = useState(true)

  const wrapperRef = useRef<HTMLDivElement>(null)
  const profilesSlider = useRef<HTMLDivElement>(null)
  const { pathname, search } = useLocation()
  const { isAuth } = useAuth()
  const params = useParams()

  const body = data?.data
  const profileId = params['*']

  const openStore = useStore()
  const navigate = useNavigate()
  const { showAlert } = useContext(AlertContext)
  useClickOutside(wrapperRef, () => {
    setDropdownVisible(false)
  })

  const checkPreviousPage = () => {
    const query = new URLSearchParams(search)
    const previousPage = query.get('from')

    if (previousPage === 'qr') {
      setCandleButtonVisible(true)
      setModal('fromQr')
    }
  }

  const fetchProfile = async (password?: string) => {
    setLoading(true)
    const profileId = pathname.split('/profile/')[1]

    try {
      const body = password ? { password } : undefined
      const response = await POST(`${urls.boarddetails}/${profileId}`, { body })
      if (response.ok) {
        setData(response.data)
      } else if (response.status === 400 || response.status === 404) {
        navigate(`/unpublished/${profileId}`)
      } else {
        throw new Error('Failed to fetch profile')
      }
    } catch (err) {
      console.error(err)
    }
    setLoading(false)
  }

    const fetchBook = async (page: number = 1) => {
      const profileId = pathname.split('/profile/')[1]
      try {
        const response = await GET(`${urls.addPost(profileId)}?page=${page}`)
        if (response.ok) {
          const data: book.BookResponse = response.data
          if (data) {
            if (page === 1) {
              setPost(data.data)
            } else {
              setPost(prevNotifications => [
                ...(prevNotifications || []),
                ...data.data
              ])
            }
            setPostHasMore(data.meta.pagination.current_page < data.meta.pagination.total_pages)
          }
        } else {
          throw new Error('Failed to fetch more post')
        }
      } catch (err) {
        console.error(err)
      }
    }

    const loadMorePosts = () => {
      if (postHasMore) {
        setPostPage(prevPage => prevPage + 1)
        fetchBook(postPage + 1)
      }
    }

  useEffect(() => {
    checkPreviousPage()
    fetchProfile()
    fetchBook()
    handleShowShare()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search])

  const handleShowShare = () => {
    setTimeout(() => {
      setShareVisible(false)
      setTimeout(() => {
        setShareVisible(true)
        setTimeout(() => {
          setShareVisible(false)
        }, 4000)

      }, 800)
    }, 0)
  }

  useEffect(() => {
    if (body?.myCandleExpiration) {
      const interval = setInterval(() => {
        const now = new Date().getTime()
        const expirationDate = new Date(body.myCandleExpiration).getTime()
        const timeDifference = expirationDate - now

        if (timeDifference > 0) {
          const hours = Math.floor((timeDifference / (1000 * 60 * 60)) % 24)
          const minutes = Math.floor((timeDifference / (1000 * 60)) % 60)

          setTimeLeft(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`)
        } else {
          setTimeLeft('00:00')
          clearInterval(interval)
        }
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [body?.myCandleExpiration, isModal])

  useEffect(() => {
    if (isModal === '') {
      handleShowShare()
    }
  }, [isModal])

  useEffect(() => {
    if (!isLoading && profilesSlider.current) {
      const selectedChild = getSelectedChild(profilesSlider.current.children)
      if (selectedChild) {
        const position = selectedChild.getBoundingClientRect()
        profilesSlider.current.scrollTo({ top: 0, left: position.left - 40 })
      }
    }
  }, [isLoading])

  const handleRefresh = () => {
    fetchProfile()
  }

  const parseDate = (dateString: string): DateParts => {

    const months = [
      t('january'), t('february'), t('march'), t('april'), t('may'), t('june'),
      t('july'), t('august'), t('september'), t('october'), t('november'), t('december')
    ]

    const [year, month, day] = dateString.split('-')
    const monthName = months[parseInt(month, 10) - 1]

    return {
      day,
      monthName,
      year
    }
  }

  const birthParts = body?.birth ? parseDate(body.birth) : { day: '', monthName: '', year: '' }
  const deathParts = body?.death ? parseDate(body.death) : { day: '', monthName: '', year: '' }

  const photosFromGallery = body?.gallery.filter(item => (item.isImage && item.isPublished))
  const videosFromGallery = body?.gallery.filter(item => (item.isVideo && item.isPublished))

  const hideModal = () => {
    setModal('')
  }

  const buyQr = () => {
    if (!body?.to_order) return
    const { product_key, params } = body.to_order
    openStore(product_key, { related: params.related, profileName: body.name })
  }

  const editProfile = async (qr_id: string, profile_id: string) => {
    if (isLoading) return
    setLoading(true)
    try {
      const response = await POST(urls.updatedBoard(profile_id), {
        body: { qr_id }
      })

      if (response.ok) {
        showAlert('Zaktualizowano profil!', 'success')
        if (window.location.search.includes('scanner')) {
          navigate(window.location.pathname, { replace: true })
        } else {
          setData(response.data)
          setLoading(false)
        }
      } else {
        throw new Error(':/')
      }
    } catch (error) {
      setLoading(false)
      showAlert('Błąd w trakcie aktualizowania!', 'error')
    }
  }

  if (isScanner) {
    return <QrScanner
      setQrData={({ is_owned, qrState, id }) => {
        if (!id || !profileId) {
          showAlert('Niewłaściwy tabliczka!', 'error')
        } else if (!is_owned && qrState === 'occupied') {
          showAlert('Nie jesteś właścicielem tabliczki!', 'error')
        } else {
          editProfile(id, profileId)
        }
        showScanner(false)
      }}
    />
  }

  const isEnglish = () => {
    if (language === 'en') return true
    else return false
  }

  return (
    <>
      {(isModal === 'loginRequired' || isModal === 'scannerLoginRequired') && <LoginRequiredModal
        customRedirect={isModal === 'scannerLoginRequired' ? { pathname: `${window.location.pathname}?scanner=1` } : undefined}
        onClose={hideModal} />}
      {isModal === 'post' && <AddPostModal
        onClose={hideModal}
        onAddBook={handleRefresh} />}
      {isModal === 'fromQr' && <FromQrModal
        onClose={hideModal}
        name={body?.name}
        age={body?.years}
        gender={body?.gender}
        setModal={setModal}
        isLoading={isLoading} />}
      {(isModal === 'location' && body?.id) && <AddGpsModal
        boardId={body.id}
        onClose={hideModal}
        reload={handleRefresh} />}
      {isModal === 'save' && <SaveProfileModal
        onClose={hideModal}
        data={data}
        setModal={setModal}
        onReload={handleRefresh}
        type={saveType} />}
      {isModal === 'candle' && <CandlesModal
        body={data}
        timeLeft={timeLeft}
        onClose={hideModal}
        onAddCandle={handleRefresh} />}
      {((isModal === 'gallery' && photosFromGallery) || (isModal === 'galleryVideo' && videosFromGallery)) && <SwiperGalleryModal
        initialSlide={initialSlide}
        data={(isModal === 'gallery' ? photosFromGallery : videosFromGallery)!}
        onClose={hideModal}
        fromPublic />}
      {isModal === 'addphotos' && <AddPhotosModal
        id={body?.id}
        onClose={hideModal}
        fromPublic />}
      {isModal === 'publish' && body && <SetPublishModal
        boardID={body?.id}
        isPublish={body.is_published}
        onClose={hideModal}
        onReload={handleRefresh} />}

      <div className='profile'>
        {isLoading
          ? <Skeleton type='profile' />
          : <>
            <TopBar isSticky={false} theme='' isLanguage />

            {body?.linkedBoards && body.linkedBoards.length > 1 && (
              <div
                ref={profilesSlider}
                className="chooseProfile">
                {body.linkedBoards.map(linked => {
                  const shouldDisplay = linked.isOwned || linked.is_published
                  return (
                    shouldDisplay && (
                      <div
                        key={linked.id}
                        className={`btn btn--round ${linked.id === profileId ? 'active' : ''}`}
                        onClick={() => {
                          navigate(linked.name === 'Uzupełnij' ? `/addprofile/${linked.id}/update` : `/profile/${linked.id}`)
                        }}
                      >
                        {!linked.is_published && <i className="icon icon--visibility-off"></i>}
                        {linked.name}
                      </div>
                    )
                  )
                })}

                {body.isOwned && (
                  <div className="btn btn--round add" onClick={() => navigate(`/addprofile/${data?.data.qr_id}/add`)}>
                    <i className="icon icon--qr-code"></i>
                    {t('add_a_person_to_this_QR_code')}
                  </div>
                )}
              </div>
            )}


            <div className="wrap">

              <div className="profile__image">
                <div className="imgWrap">
                  {body?.img !== null
                    ? <img src={body?.img} alt="" />
                    : <p>{t('no_photo')}</p>}
                </div>
                <div className="save">
                  <ShareButton
                    text={`Kliknij w link, aby przejść do profilu ${body?.name}`}
                    url={`https://app.mojekondolencje.pl/profile/${profileId}`}
                    title='Moje Kondolencje'
                    active={isShareVisible} />
                </div>

                {body?.is_demo
                  ? <div
                    className="profile__alert"
                    onClick={() => {
                      openStore('qr_board')
                    }}>
                    <div className="profile__alert__left">
                      <h2>{t('sample_profile')}</h2>
                      <span>{t('buy_a_qr_plaque_to_create_a_profile_for_your_loved_one')}</span>
                    </div>
                    <div className="profile__alert-right">
                      <div className="iconWrap">
                        <i className="icon icon--cart"></i>
                      </div>
                    </div>
                  </div>
                  : body?.isOwned && body?.is_paid && !body?.is_published
                    ? <div className="profile__alert" onClick={() => setModal('publish')}>
                      <div className="profile__alert__left">
                        <h2>{t('profile_not_published')}</h2>
                        <span>{t('click_to_publish_the_profile_for_your_loved_ones')}</span>
                      </div>
                      <div className="profile__alert-right">
                        <div className="iconWrap">
                          <i className="icon icon--visibility"></i>
                        </div>
                      </div>
                    </div>
                    : body?.isOwned && !body?.is_paid
                      ? <div className="profile__alert">
                        {body.qr_id
                          ? <>
                            <div className="profile__alert__left">
                              <h2>{t('pay_for_the_profile')}</h2>
                              <span>{t('to_publish_the_profile_for_your_loved_ones')}</span>
                            </div>
                            <div className="profile__alert-right">
                              <div
                                className="iconWrap"
                                onClick={buyQr}>
                                <i className="icon icon--cart"></i>
                              </div>
                            </div>
                          </>
                          : <div className='profile__alert__main'>
                            <div>
                              <div className="profile__alert__left">
                                <h2>{t('pay_for_the_profile_or_scan_your_existing_plaque')}</h2>
                              </div>
                              <div className="profile__alert-right">
                                <div
                                  className="iconWrap"
                                  onClick={buyQr}>
                                  <i className="icon icon--cart"></i>
                                </div>
                                <div
                                  className="iconWrap"
                                  onClick={() => {
                                    if (isAuth) showScanner(true)
                                    else setModal('scannerLoginRequired')
                                  }}>
                                  <i className="icon icon--qr-code"></i>
                                </div>
                              </div>
                            </div>
                            <span>{t('to_publish_the_profile_for_your_loved_ones')}</span>
                          </div>}
                      </div>
                      : !body?.isOwned && !body?.myCandleExpiration
                        ? <div
                          className="profile__alert"
                          onClick={() => {
                            setModal('candle')
                          }}>
                          <div className="profile__alert__left">
                            <h2>{t('light_a_candle')}</h2>
                            <span>{t('the_deceaseds_family_will_know_you_were_here')}</span>
                          </div>
                          <div className="profile__alert-right">
                            <div className="iconWrap">
                              <i className="icon icon--candle"></i>
                            </div>
                          </div>
                        </div>
                        : undefined
                }
              </div>

              <div className="wrapRight">
                <div className="profile__important">
                  <div className="profile__important__wrap">
                    <div className="profile__important__desc">
                      <div>
                        <span>{body?.sentence}</span>
                        <div className="dot"></div>
                        <span>
                          {isEnglish()
                            ? `Aged ${body?.years !== 0 ? `${body?.years} years` : `${body?.days} days`}`
                            : `Żył${body?.gender === 'female' ? 'a' : ''} lat ${body?.years !== 0 ? body?.years : body?.days} ${body?.years !== 0 ? 'lat' : 'dni'}`}
                        </span>
                      </div>
                      <h2>{body?.name}</h2>
                    </div>
                    <div className={`profile__important__candle ${body?.myCandleExpiration !== null ? 'disabled' : undefined}`}>
                      <div onClick={() => setModal('candle')}><i className="icon icon--candle"></i>{body?.myCandleExpiration ? timeLeft : body?.candlesAmount}</div>
                    </div>
                    <div className="profile__important__timeline">
                      <div className="top">
                        <span></span><hr /><span></span>
                      </div>
                      <div className="bottom">
                        <div>
                          <span>{birthParts.day} {birthParts.monthName}</span>
                          <span>{birthParts.year}</span>
                        </div>
                        <div>
                          <span>{deathParts.day} {deathParts.monthName}</span>
                          <span>{deathParts.year}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="profile__actions">
                  {body?.isOwned
                    ? <Button
                      type='round'
                      text={t('manage')}
                      icon='settings'
                      action={() => navigate(`/manage/${profileId}`)} />
                    : body?.isSaved
                      ? <Button
                        type='round'
                        text={t('saved')}
                        icon='saved'
                        action={() => {
                          setSaveType('unsave')
                          setModal('save')
                        }} />
                      : <Button
                        type='round'
                        text={t('save')}
                        icon='save'
                        action={() => {
                          setSaveType('save')
                          setModal('save')
                        }} />}
                  {isCandleButtonVisible
                    ?
                    <Button
                      type='round'
                      text={t('light_a_candle')}
                      icon='candle'
                      action={() => setModal('candle')} />
                    : body?.hasSavedLocation
                      ? <Button
                        type='round'
                        text={t('navigate')}
                        icon='navigate'
                        action={() => openMap(body?.gps.latitude, body?.gps.longitude)} />
                      : body?.isOwned
                        ? <Button
                          type='round'
                          text={t('add_location')}
                          icon='add-location'
                          action={() => setModal('location')} />
                        : <Button
                          type='round'
                          text={t('add_an_entry')}
                          icon='book'
                          action={() => setModal('post')} />
                  }

                  <div className='dots' ref={wrapperRef}>
                    <i className="icon icon--dots" onClick={() => setDropdownVisible(!isDropdownVisible)}></i>
                    {isDropdownVisible && <div className="dots__dropdown">
                      {!body?.isOwned && <span onClick={() => {
                        setSaveType(body?.isSaved ? 'unsave' : 'save')
                        setModal('save')
                      }}>{body?.isSaved ? t('delete_from_saved') : t('save_profile')}</span>}
                      <span onClick={() => body?.hasSavedLocation ? openMap(body?.gps.latitude, body?.gps.longitude) : setModal('location')}>{body?.hasSavedLocation ? t('navigate') : t('save_location')}</span>
                      <span onClick={() => setModal('addphotos')}>{t('add_photos_or_videos')}</span>
                      <span onClick={() => setModal('post')}>{t('add_an_entry_to_the_book')}</span>
                    </div>}
                  </div>

                </div>
              </div>
            </div>
            <Tabs
              body={data}
              activeTab={activeTab}
              setModal={setModal}
              setActiveTab={setActiveTab}
              setInitialSlide={setInitialSlide}
              unlockAccess={fetchProfile}
              post={post}
              postHasMore={postHasMore}
              loadMorePosts={loadMorePosts}
            />
            {(!body?.isOwned || body?.has_footer) &&
              <div
                className='demo'
                onClick={() => {
                  window.location.href = 'https://mojekondolencje.pl'
                }}>
                <span>{t('honor_your_loved_one')}</span>
                <h4>{t('click_and_check_mojekondolencje_pl')}</h4>
              </div>}
          </>}
      </div>
    </>)
}

const getSelectedChild = (children: HTMLCollection) => {
  for (const key in children) {
    if (children[key].className?.includes('active')) {
      return children[key]
    }
  }
}

export default Profile